// Organism choreography — the pure rules behind how the living organism moves
// between dashboard sections. The 3×3 anchor grid is numbered in reading order:
//
//     1  2  3        top-left   top      top-right
//     4  5  6   =    left       center   right
//     7  8  9        bottom-left bottom  bottom-right
//
// A move that changes ONLY a row or ONLY a column is a graceful SHIFT (slide on
// that one axis). A move that changes BOTH (a diagonal) is a REGROW: the organism
// de-ages/collapses to its nucleus at the source, then forms again from the
// destination with a little spin — so it never drifts diagonally across the view.

import type { Anchor } from "../../components/OrganismBackdrop";
import type { OrganismFocus } from "../../components/MemoryOrganism";
import type { PanelKind } from "./tree";

export const ANCHOR_BY_NUM: Record<number, Anchor> = {
  1: "top-left", 2: "top", 3: "top-right",
  4: "left", 5: "center", 6: "right",
  7: "bottom-left", 8: "bottom", 9: "bottom-right",
};

export const ANCHOR_NUM: Record<Anchor, number> = {
  "top-left": 1, top: 2, "top-right": 3,
  left: 4, center: 5, right: 6,
  "bottom-left": 7, bottom: 8, "bottom-right": 9,
};

export type MoveKind = "none" | "shift" | "regrow";

/** Classify a move between two anchor numbers (1..9). */
export function classify(fromN: number, toN: number): MoveKind {
  if (fromN === toN) return "none";
  const fr = Math.floor((fromN - 1) / 3), fc = (fromN - 1) % 3;
  const tr = Math.floor((toN - 1) / 3), tc = (toN - 1) % 3;
  const changedRow = fr !== tr;
  const changedCol = fc !== tc;
  if (!changedRow && !changedCol) return "none";
  // exactly one of row/col changed → single-axis slide; both → diagonal regrow
  return changedRow !== changedCol ? "shift" : "regrow";
}

/** For a SHIFT, which axis slides: 'x' (same row → horizontal) or 'y' (vertical). */
export function shiftAxis(fromN: number, toN: number): "x" | "y" {
  const sameRow = Math.floor((fromN - 1) / 3) === Math.floor((toN - 1) / 3);
  return sameRow ? "x" : "y";
}

/** Spin direction for a regrow — rolls toward the destination column. */
export function spinFor(fromN: number, toN: number): number {
  const dc = ((toN - 1) % 3) - ((fromN - 1) % 3);
  return 0.8 * (dc >= 0 ? 1 : -1);
}

export type Section = "projects" | PanelKind;

export interface SectionAnchor {
  anchor: Anchor;
  offset?: { x?: string; y?: string };
  /** organism maturity at rest in this section (drives the regrow target). */
  age: number;
  /** backdrop opacity at rest. */
  opacity: number;
  /** WHERE IN THE STACK netti sits on this section. Defaults to NETTI_Z — a
   *  section only declares this to opt OUT of the one law. */
  z?: number;
  /** how large netti stands here, for a VIEW that seats it somewhere of its own
   *  (the hook gives an anchored view 1 otherwise: it is a flat room, never the
   *  projects dive). The agents orbit uses it so the whole bloom sits inside the
   *  ring that is drawn round it. */
  scale?: number;
}

// law_netti_above_the_veil — netti is never washed by the reading veil.
//
// The dashboard's blur veils are all `backdrop-filter`, so each one frosts
// whatever is painted BENEATH it. The big one is the reading scrim
// (.os-read-scrim / .art-mobile-scrim): fixed, full-viewport, z 1, and it fades
// in on EVERY scrolled page. At the old default of 0 netti sat under it, so the
// living creature went soft and grey the moment you scrolled anything — the one
// thing on screen that is actually alive, blurred out to make room for text.
//
// 1 puts netti on the same layer as the scrim and the content surface, where DOM
// order decides: the shell renders scrim → NETTI → content, so netti paints over
// the reading veil and the content still paints over netti. That last part is
// load-bearing — the content surface is a single stacking context, so everything
// in it (the orbit's tiles at z 10/11 included) is capped at this same 1. Lift
// netti any higher and the files stop orbiting over the creature and start
// hiding behind it.
export const NETTI_Z = 1;

// …EXCEPT where nothing has to sit on top of netti.
//
// The pinned navigator's rail is itself a frosted band (.os-nav-fixed::before)
// and it lives INSIDE the content surface, so it is always painted after netti
// and always blurs it. There is no z that threads between the two — the rail
// must be above the content (or it frosts nothing) and netti must be below the
// content (or it swallows it) — and there is no backdrop-root escape either:
// `isolation: isolate` on the content surface does NOT scope a backdrop-filter
// in Chrome (verified against a striped control — the layer outside the isolated
// container blurred exactly like the one inside).
//
// So on the sections where netti stands CLEAR of the content column it goes over
// the whole of the chrome instead: above the rail (6), the corner veils (4) and
// the docks (6). The header is lifted above THIS (see OsShell) so your avatar
// still sits on netti's face rather than behind it. Only safe because those
// sections keep their matter to one side; anywhere the page puts content on top
// of netti — every orbit/grid/list level — netti stays at NETTI_Z.
export const NETTI_Z_OVER_CHROME = 7;

// law_netti_fixed_center — netti stays PLANTED. Its bottom-center is its home on
// every section and at every directory depth; moving across the dashboard never
// slides it left/right/up/down. All it may do is ZOOM (deeper dives, scaleForDepth)
// or SPIN (dive/sibling, spinAccum) — never translate. So every section resolves
// to the SAME anchor + the SAME offset; only `age` and `opacity` differ per section
// (the organism matures / fades, it does not move). Because classify()/shiftAxis()
// key purely on the anchor number, an identical anchor makes every move a "none" —
// no shift, no diagonal de-age/regrow — so the center is truly pinned.
// THREE sanctioned exceptions (John), all of them surfaces where the content
// itself is a column that wants the rest of the width:
//   • ACTIVITY reads as a left-flowing river → netti steps to CENTER-RIGHT.
//   • the project TIMELINE is the same river of a single project's history, so
//     it reads the same way (see TIMELINE_ANCHOR below).
//   • YOU puts netti in the TOP-RIGHT, seated over your avatar — the page is
//     about you, so the organism sits where your own face already is.
//   • the REPO view (a code project's git graph, and the activity panel's repos
//     list) seats netti BOTTOM-LEFT: the branch lanes grow up out of it, so the
//     history visibly comes out of netti (see REPO_ANCHOR below).
// (A fourth, the agents orbit at the centre of the viewport, was retired on
// 2026-10-12: the orbit rises from netti's own home seat as half rings, and the
// seat stays planted; see AGENTS_ORBIT_ANCHOR.)
// A per-VIEW exception is looked up in VIEW_ANCHOR by the hook's `viewAnchor`.
// Each distinct anchor makes classify() see a real move, and the shift/regrow
// machinery animates the relocation both ways.
const HOME_ANCHOR: Anchor = "bottom";
const HOME_OFFSET = { y: "-8vh" } as const;

// The avatar's centre, measured from the viewport's top-right corner and
// published by lib/os/useAvatarAnchor. OrganismBackdrop places the organism's
// CENTRE at anchor + offset, and the organism's own centre IS its central cell —
// so this seats the cell on the face. Measured rather than computed because the
// avatar's size, the gutter and the header's height all move independently (see
// that hook). The fallbacks are the desktop resting values, used only for the
// first frame before the measurement lands.
export const AVATAR_OFFSET = {
  x: "var(--os-avatar-dx, -3.6rem)",
  y: "var(--os-avatar-dy, 2.6rem)",
} as const;

export const SECTION_ANCHOR: Record<Section, SectionAnchor> = {
  // SKILLS — a quiet index of what you can teach; netti stays planted and lets
  // the list read.
  skills:      { anchor: HOME_ANCHOR, offset: HOME_OFFSET, age: 0.56, opacity: 0.7 },
  // YOU — netti rides up to the top-right and settles over your avatar, and it
  // has to be VISIBLE there: the top-right is the one corner that is solid
  // chrome, so netti keeps a z of its own to thread over the content column and
  // its frosted band while staying under the header (5) — the avatar sits ON
  // netti's face rather than behind it.
  you:         { anchor: "top-right", offset: AVATAR_OFFSET, age: 0.62, opacity: 0.72, z: 3 },
  // NETTI — the observation space. Fully present (opacity 1, natural age) but still
  // planted: it grows into the room via presence, not by relocating.
  netti:       { anchor: HOME_ANCHOR, offset: HOME_OFFSET, age: 0.68, opacity: 1 },
  projects:    { anchor: HOME_ANCHOR, offset: HOME_OFFSET, age: 0.55, opacity: 0.82 },
  // MEMORY — figures and ledgers to read: netti stays planted and a little back, the way it does for settings
  memory:      { anchor: HOME_ANCHOR, offset: HOME_OFFSET, age: 0.6,  opacity: 0.7 },
  // ACTIVITY — the river runs down the LEFT, netti stands at centre-right with
  // nothing over it, so it goes above the whole chrome: the rail stops frosting
  // its crown (see NETTI_Z_OVER_CHROME).
  activity:    { anchor: "right", offset: { x: "-4vw" }, age: 0.5,  opacity: 0.64, z: NETTI_Z_OVER_CHROME },
  tasks:       { anchor: HOME_ANCHOR, offset: HOME_OFFSET, age: 0.5,  opacity: 0.58 },
  parked:      { anchor: HOME_ANCHOR, offset: HOME_OFFSET, age: 0.42, opacity: 0.6 },
  connections: { anchor: HOME_ANCHOR, offset: HOME_OFFSET, age: 0.58, opacity: 0.78 },
  // AGENTS — the same planted seat as connections: a list of the things that read and
  // grow this memory, with nothing for netti to step aside from.
  agents:      { anchor: HOME_ANCHOR, offset: HOME_OFFSET, age: 0.58, opacity: 0.78 },
  // OURS — the shared-spaces overview: each space carries its OWN netti, so the
  // personal organism recedes a little and lets the rings speak.
  ours:       { anchor: HOME_ANCHOR, offset: HOME_OFFSET, age: 0.52, opacity: 0.5 },
  settings:    { anchor: HOME_ANCHOR, offset: HOME_OFFSET, age: 0.5,  opacity: 0.66 },
};

// The project TIMELINE view (scroll→age commit history) is a VIEW inside the
// `projects` section, and it is the ACTIVITY river narrowed to one project — a
// left-flowing column of what happened, scrubbing the organism's age as you
// scroll. So it reads the way activity reads: netti steps to CENTER-RIGHT and
// leaves the column its width. Same anchor as `activity`, so moving between the
// two is a "none" and netti simply stays put. The hook substitutes this when
// `viewAnchor` is "timeline" (VIEW_ANCHOR).
export const TIMELINE_ANCHOR: SectionAnchor = {
  anchor: "right",
  offset: { x: "-4vw" },
  age: 0.55,
  opacity: 0.7,
  // same river, same clearance → netti rides over the chrome here too.
  z: NETTI_Z_OVER_CHROME,
};

// The REPO view — a code project's commits as branch lanes, newest at the top,
// the default lane running down the left gutter and bending into the bottom-left
// corner. netti sits THERE, so the graph reads as growing out of it. The offset
// is the one the view's fixed "tail" path aims at (left 3vw · bottom 3vh), so
// the line lands on netti's centre cell — tucked into the corner so its core
// never sits on the commit text; the lanes still visibly grow out of it. z stays NETTI_Z: the list and its lanes
// are drawn over and around netti, never under the chrome.
export const REPO_ANCHOR: SectionAnchor = {
  anchor: "bottom-left",
  offset: { x: "3vw", y: "-3vh" },
  age: 0.5,
  opacity: 0.72,
};

// The AGENTS ORBIT — netti at its HOME seat, bigger, in full focus.
//
// The agents home draws every agent a person keeps on concentric HALF rings of cell
// thread that rise from netti (components/agents/ArcOrbit, the deck's arcs), so netti
// is the subject here rather than the ground: it stays exactly where it always is,
// bottom centre (the same anchor and offset as every section, so moving into the orbit
// is a "none": it only grows), a good deal larger, sharp, at full presence. The orbit is
// drawn round this exact point, so the point is ONE constant that both sides read: the
// choreography seats netti's heart here, the orbit centres its rings on it (a fraction of
// the viewport, x then y). Change it here and both move.
//
// Only the agents HOME (/agents, depth 1) wears this, and only while the panel reports
// its orbit view (setPanelView("orbit")). An agent's own page (/agents/<slug>, depth 2+) is
// a page to read, so netti goes back to its normal size at the soft deep focus.
//
// Until 2026-10-12 this was the CENTRE of the viewport at 0.58, with full rings drawn round
// it and a spoke to every agent; the half rings replaced both (John).
export const AGENTS_CENTER = { x: 0.5, y: 0.92 } as const;

/** Where netti's heart is on screen RIGHT NOW, in viewport pixels, for the orbit that
 *  is drawn round it. It is AGENTS_CENTER of the organism's field, and that field is
 *  a fixed inset-0 layer: it is as wide as the document BODY (a classic scrollbar,
 *  which the page has whenever it overflows, takes its 15px from both) and as tall as
 *  the viewport. So the orbit must not assume `innerWidth / 2`: on a page that scrolls
 *  that is 7px right of netti, and it changes by itself when the page stops
 *  overflowing. Pass the two sizes in to compute it for any box (the tests do). */
export function agentsHeart(box?: { width: number; height: number }): { x: number; y: number } {
  const hasDom = typeof document !== "undefined" && typeof window !== "undefined";
  const width = box?.width ?? (hasDom ? document.body.clientWidth || window.innerWidth : 0);
  const height = box?.height ?? (hasDom ? window.innerHeight : 0);
  return { x: width * AGENTS_CENTER.x, y: height * AGENTS_CENTER.y };
}
/** the shell shrinks netti by this on a phone (its usual 0.8 is a little small here: the
 *  rings rise a long way and the bloom is their ground). With the anchor's own scale it
 *  makes the phone's bloom ~0.85 of the full one. */
export const AGENTS_ORBIT_NARROW = 0.77;
export const AGENTS_ORBIT_ANCHOR: SectionAnchor = {
  anchor: HOME_ANCHOR,
  offset: HOME_OFFSET,
  age: 0.68,
  opacity: 1,
  // bigger than anywhere else on the dashboard: the longest petal tips reach ~0.32 of the drawn
  // box (about 400px at 1440×900 at this scale), the inner ring runs over the petals and the
  // tiles ride it, as in the deck.
  scale: 1.12,
};

/** the per-VIEW anchors (the hook's `viewAnchor` key → where netti goes). A view
 *  not listed here keeps its section's anchor. */
export const VIEW_ANCHOR: Record<string, SectionAnchor> = {
  timeline: TIMELINE_ANCHOR,
  repo: REPO_ANCHOR,
  agents: AGENTS_ORBIT_ANCHOR,
};

/** On a short window the half rings have less room above netti's heart, but the organism's
 *  box hardly shrinks (it is min(150vh, 1120px)), so the bloom would crowd them and push the
 *  tiles onto the petals. In the orbit view netti scales with the window's height: 1 at 900px
 *  and taller, down to 0.68 on a 640px window. The orbit's own geometry reads the same factor
 *  (bloomReach), so the inner ring always keeps off the heart. */
export function agentsOrbitShort(vh: number): number {
  return Math.min(1, Math.max(0.68, (vh - 160) / 740));
}

/** Is netti standing in the agents orbit (bigger, at its home seat)? The shell's one rule, kept
 *  here so the tree, the shell and the tests say it the same way: the agents HOME (one segment
 *  deep) while the panel reports its orbit view. */
export function agentsOrbitUp(path: string, panelView: string | null | undefined, today = false): boolean {
  const segs = path.split("/").filter(Boolean);
  // the agents home only. (2026-10-15 → 10-22 the connections space drew the same orbit; the agents
  // left connections for a space of their own, and connections has no orbit any more.) `today`
  // (lib/release/next: everyone but a test account) keeps the connections orbit until the press.
  return segs.length === 1 && (segs[0] === "agents" || (today && segs[0] === "connections")) && panelView === "orbit";
}

/** the anchor for a section, or the view's own when it has one. */
export function anchorFor(section: Section, viewAnchor?: string | null): SectionAnchor {
  if (viewAnchor && Object.prototype.hasOwnProperty.call(VIEW_ANCHOR, viewAnchor)) return VIEW_ANCHOR[viewAnchor];
  return SECTION_ANCHOR[section];
}

const PANEL_SECTIONS = new Set<string>(["you", "memory", "ours", "activity", "parked", "connections", "agents", "settings", "skills"]);

/** The dashboard section a path belongs to (first path segment). */
export function sectionFromPath(path: string): Section {
  const seg0 = path.split("/").filter(Boolean)[0] ?? "projects";
  return PANEL_SECTIONS.has(seg0) ? (seg0 as PanelKind) : "projects";
}

/** Depth-driven scale for the bottom-center directory dive (projects only).
 *  (Task 4) CAPPED so deep directories never balloon: the per-level growth is
 *  gentler (0.14 → 0.07/level) and the total dive growth is held to +0.22 (≈1.22×)
 *  instead of +0.5 — the organism stays graceful and legible at any depth. */
export function scaleForDepth(section: Section, depth: number, inViewer: boolean): number {
  if (section !== "projects") return 1;
  return 1 + Math.min(0.22, Math.max(0, depth - 1) * 0.07) + (inViewer ? 0.04 : 0);
}

/** Spin-by-depth for the dive (radians). */
export function rotationForDepth(section: Section, depth: number): number {
  if (section !== "projects") return 0;
  return (depth - 1) * 0.5;
}

/** Focus blob that BRIGHTENS a different sector of the organism as you descend
 *  directories — the origin stays put (focusLeaf no longer pans the camera). The
 *  sibling index picks which sector lights up so folders feel distinct, and the
 *  zoom is a GENTLE scale-about-center (never a pan): the organism spreads and
 *  highlights, it does not slide. zoom stays subtle (≤1.12) so a deep dive reads
 *  as a soft spread, not a balloon; dist (the highlight's reach) is capped so the
 *  lit sector stays near the crisp core. */
export function focusForDepth(depth: number, activeIdx: number): OrganismFocus | null {
  if (depth <= 1) return null; // dashboard / project root → whole organism
  const angle = 0.35 + activeIdx * 0.9;
  const dist = Math.min(0.46, 0.18 + (depth - 1) * 0.14);
  const zoom = Math.min(1.12, 1.0 + (depth - 1) * 0.06);
  return { angle, dist, zoom };
}
